import { api } from '@/api/client'

export interface AlertTeam {
  id: number
  name: string
  description: string | null
  is_active: number | boolean
}

export interface AlertCoverage {
  client: string
  environment: string | null
  severity_min: number // SEVERITY_RANK interno 0..3
  channels: string[]
  notify_resolved: boolean
}

export interface AlertMute {
  id: number
  scope_key: string
  muted_until: string
  created_at: string
}

export const alertTeamsApi = {
  list: () =>
    api.get<{ items: AlertTeam[] }>('/v1/admin/alert-teams').then((r) => r.data.items),

  create: (payload: { name: string; description?: string | null }) =>
    api.post<{ id: number; name: string }>('/v1/admin/alert-teams', payload).then((r) => r.data),

  deactivate: (id: number) =>
    api.delete(`/v1/admin/alert-teams/${id}`).then((r) => r.data),

  addMember: (id: number, userId: number) =>
    api.post(`/v1/admin/alert-teams/${id}/members`, { user_id: userId }).then((r) => r.data),

  removeMember: (id: number, userId: number) =>
    api.delete(`/v1/admin/alert-teams/${id}/members/${userId}`).then((r) => r.data),

  setCoverage: (id: number, c: AlertCoverage) =>
    api
      .post<{ rule_id: number }>(`/v1/admin/alert-teams/${id}/coverage`, c)
      .then((r) => r.data),
}

export interface HeartbeatSource {
  id: number
  source: string
  stream: string
  window_seconds: number
  last_signal_at: string | null
  last_alert_at: string | null
  is_active: number | boolean
  created_at: string
  /** Estado operacional calculado pelo servidor, pela MESMA regra do vigia. */
  atrasada: boolean
  atraso_segundos: number | null
  nunca_sinalizou: boolean
}

export const heartbeatSourcesApi = {
  list: () =>
    api
      .get<{ items: HeartbeatSource[] }>('/v1/admin/heartbeat-sources')
      .then((r) => r.data.items),

  create: (payload: {
    source: string
    stream?: string
    window_seconds: number
    is_active?: boolean
  }) =>
    api
      .post<{ id: number; source: string }>('/v1/admin/heartbeat-sources', payload)
      .then((r) => r.data),

  update: (id: number, payload: { window_seconds?: number; is_active?: boolean }) =>
    api.put(`/v1/admin/heartbeat-sources/${id}`, payload).then((r) => r.data),

  remove: (id: number) =>
    api.delete(`/v1/admin/heartbeat-sources/${id}`).then((r) => r.data),
}

export const alertMutesApi = {
  list: () =>
    api.get<{ items: AlertMute[] }>('/v1/me/alert-mutes').then((r) => r.data.items),

  create: (payload: { scope_key: string; hours: number }) =>
    api.post<{ id: number }>('/v1/me/alert-mutes', payload).then((r) => r.data),

  remove: (id: number) =>
    api.delete(`/v1/me/alert-mutes/${id}`).then((r) => r.data),
}

/** Mapa rank interno → string Severity aceita pelo backend SubscriptionUpsert. */
const _RANK_TO_SEVERITY: Record<number, string> = {
  0: 'debug',
  1: 'warning',
  2: 'critical',
  3: 'fatal',
}

export const alertSubscriptionApi = {
  /**
   * Liga o push de alertas Zabbix (opt-in obrigatório do canal webpush).
   *
   * @param minSeverity - string Severity ('warning', 'critical', …) OU rank
   *   interno numérico legado (1=warning, 2=critical). Sempre enviado ao
   *   backend como string — SubscriptionUpsert.min_severity é Literal[str].
   */
  enableZabbixWebpush: (minSeverity: string | number = 'critical') => {
    const severityStr =
      typeof minSeverity === 'number'
        ? (_RANK_TO_SEVERITY[minSeverity] ?? 'critical')
        : minSeverity
    return api
      .patch('/v1/me/notification-subscriptions', {
        items: [{ source: 'zabbix', channel: 'webpush', enabled: true, min_severity: severityStr }],
      })
      .then((r) => r.data)
  },
}
